import React from 'react';

/**
 * Premium Confirmation Modal
 * @param {boolean} isOpen - Whether the modal is shown
 * @param {string} title - Modal title
 * @param {string} message - Description text
 * @param {string} confirmText - Text for the confirm button
 * @param {string} cancelText - Text for the cancel button
 * @param {function} onConfirm - Callback when confirmed
 * @param {function} onCancel - Callback when cancelled
 * @param {boolean} isLoading - Loading state for action button
 * @param {string} type - 'danger' | 'warning' | 'info'
 */
const ConfirmationModal = ({ 
    isOpen, 
    title = 'Are you sure?', 
    message = 'This action cannot be undone.', 
    confirmText = 'Confirm', 
    cancelText = 'Cancel', 
    onConfirm, 
    onCancel, 
    isLoading = false,
    type = 'info'
}) => {
    if (!isOpen) return null;

    const typeConfig = {
        danger: {
            icon: <svg className="w-8 h-8 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>,
            bg: 'bg-rose-50 dark:bg-rose-500/10',
            text: 'text-rose-600 dark:text-rose-400',
            button: 'bg-rose-600 hover:bg-rose-700 shadow-rose-200 dark:shadow-none'
        },
        warning: {
            icon: <svg className="w-8 h-8 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
            bg: 'bg-amber-50 dark:bg-amber-500/10',
            text: 'text-amber-600 dark:text-amber-400',
            button: 'bg-amber-600 hover:bg-amber-700 shadow-amber-200 dark:shadow-none'
        },
        info: {
            icon: <svg className="w-8 h-8 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>,
            bg: 'bg-indigo-50 dark:bg-indigo-500/10',
            text: 'text-indigo-600 dark:text-indigo-400',
            button: 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-200 dark:shadow-none'
        }
    };

    const config = typeConfig[type] || typeConfig.info;

    return (
        <div 
            className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-[4px] transition-opacity duration-300"
            style={{ animation: 'fadeIn 0.2s ease-out' }}
        >
            <style>{`
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes scaleIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
            `}</style>
            <div 
                className="bg-white dark:bg-slate-900 rounded-[32px] w-full max-w-[440px] overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.3)] dark:shadow-black/60 border border-transparent dark:border-white/5"
                style={{ animation: 'scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)' }}
            >
                <div className="p-10 text-center">
                    <div className={`w-20 h-20 ${config.bg} ${config.text} rounded-[28px] flex items-center justify-center text-3xl mx-auto mb-6 transform -rotate-3 hover:rotate-0 transition-transform duration-300 shadow-inner dark:bg-opacity-20`}>
                        {config.icon}
                    </div>
                    <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-3 tracking-tight">{title}</h3>
                    <p className="text-[15px] text-slate-500 dark:text-slate-400 font-medium leading-relaxed px-2">
                        {message}
                    </p>
                </div>
                <div className="flex items-center gap-4 p-8 bg-slate-50/80 dark:bg-slate-950/50 border-t border-slate-100/80 dark:border-slate-800">
                    <button 
                        onClick={onCancel}
                        disabled={isLoading}
                        className="flex-1 px-6 py-4 rounded-2xl text-[15px] font-bold text-slate-500 dark:text-slate-400 hover:bg-slate-200/50 dark:hover:bg-slate-800 hover:text-slate-800 dark:hover:text-white transition-all active:scale-95 disabled:opacity-50"
                    >
                        {cancelText}
                    </button>
                    <button 
                        onClick={onConfirm}
                        disabled={isLoading}
                        className={`flex-[1.5] px-6 py-4 rounded-2xl text-[15px] font-bold text-white shadow-xl transition-all active:scale-[0.97] hover:brightness-110 disabled:opacity-70 flex items-center justify-center gap-3 ${config.button}`}
                    >
                        {isLoading ? (
                            <>
                                <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin" />
                                <span>Processing...</span>
                            </>
                        ) : (
                            confirmText
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmationModal;
